/**
 * AudioTranscriber - bouton "enregistrer / charger un audio" qui transcrit
 * automatiquement via /api/audio/transcribe (Whisper) et appelle onTranscribed(text).
 *
 * Trois modes :
 * 1. Upload d'un fichier audio (.mp3, .ogg, .wav, etc.)
 * 2. Enregistrement live au micro du navigateur (MediaRecorder API)
 * 3. Affichage pendant la transcription (loading)
 */
import React, { useState, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AudioTranscriber = ({ onTranscribed, label = "Transcrire un audio" }) => {
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const transcribe = async (file) => {
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file, file.name || 'audio');
      const r = await axios.post(`${API}/audio/transcribe`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const text = r.data?.text || '';
      if (text) onTranscribed(text);
      else setError("Transcription vide");
    } catch (e) {
      setError(e.response?.data?.detail || "Echec de la transcription");
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) transcribe(f);
    e.target.value = '';
  };

  const startRecording = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || 'audio/webm' });
        const ext = (blob.type.includes('webm')) ? '.webm' : (blob.type.includes('ogg') ? '.ogg' : '.mp3');
        const file = new File([blob], `record${ext}`, { type: blob.type });
        // Stop micro tracks
        stream.getTracks().forEach(t => t.stop());
        transcribe(file);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      setError("Microphone non accessible : " + (e.message || ''));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="audio-transcriber">
      <input ref={fileInputRef} type="file" accept="audio/*,.ogg,.oga,.mp3,.m4a,.wav,.webm"
             onChange={handleFile} className="hidden"
             data-testid="audio-file-input" />

      <button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy || recording}
              className="px-3 py-1.5 text-xs font-semibold border border-[#008A8D] text-[#008A8D] rounded-lg hover:bg-[#008A8D]/10 disabled:opacity-50 flex items-center gap-1.5"
              data-testid="audio-upload-btn">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
        Charger un audio
      </button>

      {!recording ? (
        <button type="button" onClick={startRecording} disabled={busy}
                className="px-3 py-1.5 text-xs font-semibold bg-[#008A8D] text-white rounded-lg hover:bg-[#006B6D] disabled:opacity-50 flex items-center gap-1.5"
                data-testid="audio-record-btn">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-14 0m7 7v3m-3 0h6M12 3a3 3 0 00-3 3v5a3 3 0 006 0V6a3 3 0 00-3-3z" /></svg>
          Enregistrer
        </button>
      ) : (
        <button type="button" onClick={stopRecording}
                className="px-3 py-1.5 text-xs font-semibold bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-1.5 animate-pulse"
                data-testid="audio-stop-btn">
          <span className="w-2 h-2 bg-white rounded-full"></span>
          Stop & Transcrire
        </button>
      )}

      {busy && (
        <span className="text-xs text-[#008A8D] flex items-center gap-1.5" data-testid="audio-busy">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z"></path></svg>
          Transcription en cours...
        </span>
      )}

      {error && (
        <span className="text-xs text-red-500 italic" data-testid="audio-error">{error}</span>
      )}
    </div>
  );
};

export default AudioTranscriber;
